import React, { useCallback, useEffect, useState } from 'react';
import { Check, Copy, Link2, Loader2, LogOut, RefreshCw, UserMinus, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  crearLinkEquipo,
  desactivarLinkEquipo,
  EquipoError,
  fetchEquipo,
  sacarMiembro,
  salirDelEquipo,
  urlDeInvitacion,
  type Equipo as EquipoData,
} from '../../lib/equipoApi';
import { CACHE_KEYS, invalidateCache } from '../../utils/sessionCache';
import { puedeInvitarEquipo } from '../../lib/planCapabilities';

interface EquipoProps {
  /** `company_profiles.plan`; Settings ya lo baja. */
  plan: string;
  /** `profiles.id` del usuario en sesión, para marcar "(vos)" en la lista. */
  miId: string;
  setError: (error: string | null) => void;
  setSuccess: (success: string | null) => void;
}

/**
 * Sección "Equipo" de Configuración — spec 2026-09-11-invitaciones-equipo §4.1.
 * El dueño ve el link (copiar / regenerar / desactivar) y saca gente; el miembro ve la
 * lista y puede salir. Sin card propia: el recuadro blanco lo pone Settings.
 */
const fecha = (iso: string) => new Date(iso).toLocaleDateString('es-AR', { day: 'numeric', month: 'numeric' });

const BOTON =
  'inline-flex items-center gap-1.5 py-2 px-4 rounded-md text-[14px] font-medium transition-colors disabled:opacity-60';
const PRIMARIO = `${BOTON} bg-brand-primary text-white hover:opacity-90`;
const SECUNDARIO = `${BOTON} bg-white border border-neutral-200 text-brand-black/90 hover:bg-neutral-50`;

export const Equipo: React.FC<EquipoProps> = ({ plan, miId, setError, setSuccess }) => {
  const navigate = useNavigate();
  const [equipo, setEquipo] = useState<EquipoData | null>(null);
  const [cargando, setCargando] = useState(true);
  const [trabajando, setTrabajando] = useState(false);
  const [copiado, setCopiado] = useState(false);
  // Sólo Ultra invita (ADR-2026-09-12-equipo-solo-ultra); el resto ve el bloque con el CTA.
  const sinEquipo = !puedeInvitarEquipo(plan);

  const cargar = useCallback(async () => {
    try {
      setEquipo(await fetchEquipo());
    } catch {
      setError('No pudimos cargar tu equipo. Probá de nuevo.');
    } finally {
      setCargando(false);
    }
  }, [setError]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const correr = async (accion: () => Promise<void>, exito: string) => {
    setTrabajando(true);
    setError(null);
    setSuccess(null);
    try {
      await accion();
      await cargar();
      setSuccess(exito);
    } catch (e) {
      setError(e instanceof EquipoError ? e.message : 'No se pudo completar. Probá de nuevo.');
    } finally {
      setTrabajando(false);
    }
  };

  const copiar = async () => {
    if (!equipo?.link) return;
    try {
      await navigator.clipboard.writeText(urlDeInvitacion(equipo.link.token));
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 1500);
    } catch {
      setError('No se pudo copiar. Seleccioná el link y copialo a mano.');
    }
  };

  const salir = async () => {
    if (!window.confirm('Vas a dejar de ver esta cuenta. Vas a poder crear tu propia cuenta. ¿Salir del equipo?')) {
      return;
    }
    await correr(async () => {
      await salirDelEquipo();
      // Lo que el front cacheó de la cuenta ya miente (spec §3.2).
      invalidateCache(CACHE_KEYS.USER_PROFILE);
      invalidateCache(CACHE_KEYS.COMPANY_DATA);
      try {
        localStorage.removeItem('atlas-user-plan');
        localStorage.removeItem('atlas-user-group');
      } catch {
        /* noop */
      }
      window.location.assign('/');
    }, 'Saliste del equipo.');
  };

  return (
    <div>
      <div className="pb-5 mb-5 border-b border-neutral-200">
        <h2 className="text-[17px] font-semibold text-brand-black/90">Equipo</h2>
        <p className="text-[13px] text-neutral-500 mt-0.5">Invitá a tu equipo a trabajar en tu cuenta</p>
      </div>

      {cargando || !equipo ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
        </div>
      ) : (
        <div className="space-y-6">
          {equipo.soyDueno && (
            <section className="p-4 bg-surface-elevated border border-neutral-200 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Link2 className="w-4 h-4 text-neutral-500" />
                <h3 className="text-[15px] font-medium text-brand-black/90">Link de invitación</h3>
              </div>
              {sinEquipo ? (
                <>
                  <p className="text-[14px] text-neutral-600">
                    Invitar a tu equipo está disponible únicamente con el plan Ultra.
                  </p>
                  <button type="button" onClick={() => navigate('/subscription')} className={`${PRIMARIO} mt-3`}>
                    Mejorar mi plan
                  </button>
                </>
              ) : equipo.link && equipo.link.estado === 'vigente' ? (
                <>
                  <div className="flex gap-2">
                    <input
                      readOnly
                      value={urlDeInvitacion(equipo.link.token)}
                      onFocus={(e) => e.currentTarget.select()}
                      className="flex-1 min-w-0 px-3 py-2 text-[14px] text-brand-black/90 bg-white border border-neutral-200 rounded-md truncate"
                    />
                    <button type="button" onClick={copiar} className={PRIMARIO}>
                      {copiado ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      {copiado ? 'Copiado' : 'Copiar'}
                    </button>
                  </div>
                  <p className="text-[13px] text-neutral-500 mt-2">
                    Vence el {fecha(equipo.link.expiraAt)}
                    {equipo.link.usos > 0 &&
                      ` · ${equipo.link.usos} ${equipo.link.usos === 1 ? 'persona entró' : 'personas entraron'} con este link`}
                  </p>
                  <div className="flex gap-2 mt-3">
                    <button
                      type="button"
                      disabled={trabajando}
                      className={SECUNDARIO}
                      onClick={() =>
                        window.confirm('El link anterior deja de servir. ¿Regenerar?') &&
                        correr(async () => {
                          await crearLinkEquipo();
                        }, 'Link nuevo listo.')
                      }
                    >
                      <RefreshCw className="w-4 h-4" /> Regenerar
                    </button>
                    <button
                      type="button"
                      disabled={trabajando}
                      className={SECUNDARIO}
                      onClick={() => correr(desactivarLinkEquipo, 'Link desactivado.')}
                    >
                      Desactivar
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-[14px] text-neutral-600">
                    {equipo.link ? `Tu link anterior venció el ${fecha(equipo.link.expiraAt)}. ` : ''}
                    Creá un link y mandáselo a tu equipo por WhatsApp o mail.
                  </p>
                  <button
                    type="button"
                    disabled={trabajando}
                    className={`${PRIMARIO} mt-3`}
                    onClick={() =>
                      correr(async () => {
                        await crearLinkEquipo();
                      }, 'Link listo. Copialo y mandáselo a tu equipo.')
                    }
                  >
                    {trabajando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                    Crear link
                  </button>
                </>
              )}
            </section>
          )}

          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-neutral-500" />
                <h3 className="text-[15px] font-medium text-brand-black/90">Miembros</h3>
              </div>
              <span className="text-[13px] text-neutral-500">
                {equipo.miembros.length} de {equipo.tope} personas
              </span>
            </div>
            <ul className="divide-y divide-neutral-200 border border-neutral-200 rounded-lg overflow-hidden">
              {equipo.miembros.map((m) => (
                <li key={m.id} className="flex items-center gap-3 px-3 py-2.5 bg-white">
                  <div className="w-8 h-8 rounded-full bg-neutral-100 text-neutral-600 flex items-center justify-center text-[13px] font-medium shrink-0">
                    {m.nombre.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] font-medium text-brand-black/90 truncate">
                      {m.nombre}
                      {m.id === miId && <span className="text-neutral-400 font-normal"> (vos)</span>}
                    </p>
                    <p className="text-[12px] text-neutral-500 truncate">{m.email ?? ''}</p>
                  </div>
                  <span
                    className={`text-[11px] px-2 py-0.5 rounded-full ${
                      m.esDueno ? 'bg-[#EBEFFE] text-brand-primary' : 'bg-neutral-100 text-neutral-600'
                    }`}
                  >
                    {m.esDueno ? 'Dueño' : 'Miembro'}
                  </span>
                  {equipo.soyDueno && !m.esDueno && (
                    <button
                      type="button"
                      disabled={trabajando}
                      title="Sacar del equipo"
                      className="p-1.5 rounded-md text-neutral-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                      onClick={() =>
                        window.confirm(`${m.nombre} deja de ver esta cuenta. Lo que creó se queda acá. ¿Sacar?`) &&
                        correr(() => sacarMiembro(m.id), `${m.nombre} ya no es parte del equipo.`)
                      }
                    >
                      <UserMinus className="w-4 h-4" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </section>

          {!equipo.soyDueno && (
            <button type="button" disabled={trabajando} onClick={salir} className={`${SECUNDARIO} text-red-600`}>
              <LogOut className="w-4 h-4" /> Salir del equipo
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Equipo;
